// sessionWarningService.ts - Modern Toast Notifications for Session Management
import { sessionManager } from './sessionManager'

// 🎨 Toast notification configuration
const TOAST_CONFIG = {
  position: 'bottom-right',
  style: 'modern',
  animations: {
    slideIn: 'slideInFromRight',
    slideOut: 'slideOutToRight',
    duration: 300
  },
  timeouts: {
    firstWarning: 10000,    // 10 seconds auto-dismiss
    finalWarning: 0,        // Persistent until action
    successMessage: 5000    // 5 seconds for success messages
  },
  maxToasts: 3,
  spacing: 12,
  zIndex: 9999
} as const

// 🎯 Toast notification interface
interface ToastNotification {
  id: string
  type: 'first-warning' | 'final-warning' | 'success' | 'info'
  title: string
  message: string
  timeLeft?: number
  actions?: ToastAction[]
  persistent?: boolean
  createdAt: number
  element?: HTMLElement
}

interface ToastAction {
  label: string
  action: () => void
  style: 'primary' | 'secondary'
}

/**
 * Session Warning Service - Modern Toast Notifications
 * Provides non-intrusive, aesthetically pleasing session warnings
 */
export class SessionWarningService {
  private container: HTMLElement | null = null
  private activeToasts = new Map<string, ToastNotification>()
  private isInitialized = false
  
  private readonly DEBUG = process.env.NODE_ENV === 'development'

  constructor() {
    this.log('SessionWarningService initialized')
  }

  /**
   * Initialize the warning service
   */
  initialize(): void {
    if (this.isInitialized) return

    this.createToastContainer()
    this.setupSessionCallbacks()
    this.injectStyles()
    this.isInitialized = true
    
    this.log('SessionWarningService started')
  }

  /**
   * Create the toast container
   */
  private createToastContainer(): void {
    this.container = document.createElement('div')
    this.container.id = 'session-toast-container'
    this.container.className = 'session-toast-container'
    
    // Add to body
    document.body.appendChild(this.container)
    
    this.log('Toast container created')
  }

  /**
   * Setup callbacks with session manager
   */
  private setupSessionCallbacks(): void {
    sessionManager.onWarning((type, timeLeft) => {
      this.handleSessionWarning(type, timeLeft)
    })

    sessionManager.onLogout(() => {
      this.clearAllToasts()
    })
  }

  /**
   * Handle session warnings from session manager
   */
  private handleSessionWarning(type: 'first' | 'final' | 'clear', timeLeft: number): void {
    switch (type) {
      case 'first':
        this.showFirstWarning(timeLeft)
        break
      case 'final':
        this.showFinalWarning(timeLeft)
        break
      case 'clear':
        this.clearWarningToasts()
        break
    }
  }

  /**
   * Show first warning (3 minutes left)
   */
  private showFirstWarning(timeLeft: number): void {
    const minutes = Math.ceil(timeLeft / 60000)
    
    const toast: ToastNotification = {
      id: 'session-first-warning',
      type: 'first-warning',
      title: 'Session Expiring Soon',
      message: `Your session will expire in ${minutes} minute${minutes !== 1 ? 's' : ''}. Any activity will extend your session.`,
      timeLeft,
      persistent: false,
      createdAt: Date.now(),
      actions: [
        {
          label: 'Extend Session',
          action: () => this.extendSession(),
          style: 'primary'
        }
      ]
    }

    this.showToast(toast, TOAST_CONFIG.timeouts.firstWarning)
  }

  /**
   * Show final warning (1 minute left)
   */
  private showFinalWarning(timeLeft: number): void {
    // Clear any existing warnings
    this.clearWarningToasts()
    
    const seconds = Math.ceil(timeLeft / 1000)
    
    const toast: ToastNotification = {
      id: 'session-final-warning',
      type: 'final-warning',
      title: 'Session Expiring Now!',
      message: `Your session will expire in ${seconds} second${seconds !== 1 ? 's' : ''}. Click to stay logged in.`,
      timeLeft,
      persistent: true,
      createdAt: Date.now(),
      actions: [
        {
          label: 'Stay Logged In',
          action: () => this.extendSession(),
          style: 'primary'
        }
      ]
    }

    this.showToast(toast, 0) // Persistent
    
    // Update countdown every second
    this.startCountdown(toast)
  }

  /**
   * Start countdown timer for final warning
   */
  private startCountdown(toast: ToastNotification): void {
    const interval = setInterval(() => {
      const sessionInfo = sessionManager.getSessionInfo()
      const timeLeft = sessionInfo.timeLeft
      
      if (timeLeft <= 0 || !this.activeToasts.has(toast.id)) {
        clearInterval(interval)
        return
      }
      
      const seconds = Math.ceil(timeLeft / 1000)
      toast.message = `Your session will expire in ${seconds} second${seconds !== 1 ? 's' : ''}. Click to stay logged in.`
      
      // Update toast content
      this.updateToastContent(toast)
    }, 1000)
  }

  /**
   * Show a toast notification
   */
  private showToast(toast: ToastNotification, autoHideMs: number = 0): void {
    if (!this.container) return

    // Remove existing toast with same ID
    this.removeToast(toast.id)

    // Create toast element
    const element = this.createToastElement(toast)
    toast.element = element

    // Add to container and map
    this.container.appendChild(element)
    this.activeToasts.set(toast.id, toast)

    // Trigger slide-in animation
    requestAnimationFrame(() => {
      element.classList.add('show')
    })

    // Auto-hide if specified
    if (autoHideMs > 0) {
      setTimeout(() => {
        this.removeToast(toast.id)
      }, autoHideMs)
    }

    this.log('Toast shown', { id: toast.id, type: toast.type })
  }

  /**
   * Create toast HTML element
   */
  private createToastElement(toast: ToastNotification): HTMLElement {
    const element = document.createElement('div')
    element.className = `session-toast session-toast-${toast.type}`
    element.setAttribute('data-toast-id', toast.id)

    const iconHtml = this.getIconHtml(toast.type)
    const actionsHtml = toast.actions ? this.createActionsHtml(toast.actions) : ''

    element.innerHTML = `
      <div class="session-toast-content">
        <div class="session-toast-header">
          ${iconHtml}
          <h4 class="session-toast-title">${toast.title}</h4>
          ${!toast.persistent ? '<button class="session-toast-close" aria-label="Close">&times;</button>' : ''}
        </div>
        <p class="session-toast-message">${toast.message}</p>
        ${actionsHtml}
      </div>
    `

    // Add close button handler
    const closeBtn = element.querySelector('.session-toast-close')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.removeToast(toast.id))
    }

    // Add action button handlers
    toast.actions?.forEach((action, index) => {
      const btn = element.querySelector(`[data-action-index="${index}"]`)
      if (btn) {
        btn.addEventListener('click', () => {
          action.action()
          if (!toast.persistent) {
            this.removeToast(toast.id)
          }
        })
      }
    })

    return element
  }

  /**
   * Create actions HTML
   */
  private createActionsHtml(actions: ToastAction[]): string {
    const buttonsHtml = actions.map((action, index) => 
      `<button class="session-toast-action session-toast-action-${action.style}" data-action-index="${index}">
        ${action.label}
      </button>`
    ).join('')

    return `<div class="session-toast-actions">${buttonsHtml}</div>`
  }

  /**
   * Get icon HTML for toast type
   */
  private getIconHtml(type: ToastNotification['type']): string {
    const icons = {
      'first-warning': '⏰',
      'final-warning': '🚨',
      'success': '✅',
      'info': 'ℹ️'
    }
    
    return `<span class="session-toast-icon">${icons[type]}</span>`
  }

  /**
   * Update toast content (for countdown)
   */
  private updateToastContent(toast: ToastNotification): void {
    if (!toast.element) return

    const messageEl = toast.element.querySelector('.session-toast-message')
    if (messageEl) {
      messageEl.textContent = toast.message
    }
  }

  /**
   * Remove a specific toast
   */
  private removeToast(id: string): void {
    const toast = this.activeToasts.get(id)
    if (!toast || !toast.element) return

    // Slide out animation
    toast.element.classList.add('hide')
    
    setTimeout(() => {
      if (toast.element?.parentNode) {
        toast.element.parentNode.removeChild(toast.element)
      }
      this.activeToasts.delete(id)
      this.log('Toast removed', { id })
    }, TOAST_CONFIG.animations.duration)
  }

  /**
   * Clear warning-specific toasts
   */
  private clearWarningToasts(): void {
    const warningIds = Array.from(this.activeToasts.keys()).filter(id => 
      id.includes('warning')
    )
    
    warningIds.forEach(id => this.removeToast(id))
  }

  /**
   * Clear all toasts
   */
  private clearAllToasts(): void {
    Array.from(this.activeToasts.keys()).forEach(id => this.removeToast(id))
  }

  /**
   * Extend session via session manager
   */
  private async extendSession(): Promise<void> {
    try {
      const success = await sessionManager.extendSession()
      
      if (success) {
        this.clearWarningToasts()
        this.showSuccessToast('Session extended successfully!')
      } else {
        this.showErrorToast('Failed to extend session. Please refresh the page.')
      }
    } catch (error) {
      this.showErrorToast('Network error. Please check your connection.')
      this.log('Session extension error', error)
    }
  }

  /**
   * Show success toast
   */
  private showSuccessToast(message: string): void {
    const toast: ToastNotification = {
      id: `success-${Date.now()}`,
      type: 'success',
      title: 'Success',
      message,
      persistent: false,
      createdAt: Date.now()
    }

    this.showToast(toast, TOAST_CONFIG.timeouts.successMessage)
  }

  /**
   * Show error toast
   */
  private showErrorToast(message: string): void {
    const toast: ToastNotification = {
      id: `error-${Date.now()}`,
      type: 'final-warning', // Reuse styling
      title: 'Error',
      message,
      persistent: false,
      createdAt: Date.now()
    }

    this.showToast(toast, 8000) // Show longer for errors
  }

  /**
   * Inject CSS styles
   */
  private injectStyles(): void {
    if (document.getElementById('session-toast-styles')) return

    const styles = document.createElement('style')
    styles.id = 'session-toast-styles'
    styles.textContent = `
      .session-toast-container {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: ${TOAST_CONFIG.zIndex};
        pointer-events: none;
        max-width: 400px;
      }

      .session-toast {
        pointer-events: all;
        background: #ffffff;
        border: 1px solid #e1e5e9;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        margin-bottom: ${TOAST_CONFIG.spacing}px;
        padding: 16px;
        transform: translateX(100%);
        opacity: 0;
        transition: all ${TOAST_CONFIG.animations.duration}ms ease-out;
        max-width: 400px;
        backdrop-filter: blur(8px);
      }

      .session-toast.show {
        transform: translateX(0);
        opacity: 1;
      }

      .session-toast.hide {
        transform: translateX(100%);
        opacity: 0;
      }

      .session-toast-first-warning {
        border-left: 4px solid #f59e0b;
      }

      .session-toast-final-warning {
        border-left: 4px solid #ef4444;
        background: #fef2f2;
      }

      .session-toast-success {
        border-left: 4px solid #10b981;
      }

      .session-toast-content {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .session-toast-header {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
      }

      .session-toast-icon {
        margin-right: 8px;
        font-size: 16px;
      }

      .session-toast-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #1f2937;
        flex-grow: 1;
      }

      .session-toast-close {
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
        color: #6b7280;
        padding: 0;
        margin-left: 8px;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .session-toast-close:hover {
        color: #374151;
      }

      .session-toast-message {
        margin: 0;
        font-size: 13px;
        color: #4b5563;
        line-height: 1.4;
        margin-bottom: 12px;
      }

      .session-toast-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
      }

      .session-toast-action {
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        border: none;
        transition: background-color 0.2s;
      }

      .session-toast-action-primary {
        background: #3b82f6;
        color: white;
      }

      .session-toast-action-primary:hover {
        background: #2563eb;
      }

      .session-toast-action-secondary {
        background: #f3f4f6;
        color: #4b5563;
        border: 1px solid #d1d5db;
      }

      .session-toast-action-secondary:hover {
        background: #e5e7eb;
      }

      @media (max-width: 480px) {
        .session-toast-container {
          left: 20px;
          right: 20px;
          bottom: 20px;
          max-width: none;
        }

        .session-toast {
          max-width: none;
        }
      }
    `

    document.head.appendChild(styles)
  }

  /**
   * Cleanup service
   */
  cleanup(): void {
    this.clearAllToasts()
    
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container)
    }
    
    const styles = document.getElementById('session-toast-styles')
    if (styles?.parentNode) {
      styles.parentNode.removeChild(styles)
    }
    
    this.isInitialized = false
    this.log('SessionWarningService cleaned up')
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.DEBUG) {
      console.log(`[SessionWarningService] ${message}`, data || '')
    }
  }
}

// Export singleton instance
export const sessionWarningService = new SessionWarningService()